import { INotificationCounts } from 'src/hooks/useNotificationCounts'
import { RedirectUrls } from 'src/types/types'

export type DashboardRole = 'host' | 'rent'

export type NavKey =
  | 'hoy'
  | 'rentas'
  | 'interesados'
  | 'espacios'
  | 'tratos'
  | 'mensajes'
  | 'inventario'
  | 'pagos'
  | 'cuenta'
  | 'cuentas'
  | 'admin'

export type NavHrefState = {
  role: DashboardRole
  /** 'all' | 'none' | team uuid — from useDashboardFilter */
  selectedTeamId: string
  selectedHostId: string | null
}

/**
 * Literal keys only — `t()` is typed against `CustomTypeOptions.resources`, so
 * a `string` here would not compile and loosening `t` is banned (CLAUDE.md →
 * i18n). Add a member here when you add a nav item.
 */
export type ShellLabelKey =
  | 'SHELL.HOY'
  | 'SHELL.RENTAS'
  | 'SHELL.INTERESADOS'
  | 'SHELL.ESPACIOS'
  | 'SHELL.TRATOS'
  | 'SHELL.MENSAJES'
  | 'SHELL.INVENTARIO'
  | 'SHELL.PAGOS'
  | 'SHELL.CUENTA'
  | 'SHELL.CUENTAS'
  | 'SHELL.ADMIN'

export type NavItemDef = {
  key: NavKey
  /** Key inside the `nav` namespace, e.g. 'SHELL.HOY'. */
  labelKey: ShellLabelKey
  /** `si-*` icon-font class per role — must exist in docs/claude/icons.md. */
  icon: (role: DashboardRole) => string
  href: (s: NavHrefState) => string
  /**
   * `useNotificationCounts` computes host AND renter counts whatever the role
   * (`rents` = host pending, `reservations` = renter pending), so a badge must
   * pick its side — summing both leaks one dashboard's work into the other.
   */
  badge: (c: INotificationCounts, role: DashboardRole) => number
}

const isTeam = (s: NavHrefState) =>
  s.selectedTeamId !== 'all' && s.selectedTeamId !== 'none'

/** Cuenta: a team scope opens that team, Personal the profile, Todas the list. */
const cuentaHref = (s: NavHrefState): string => {
  if (s.role === 'rent') return RedirectUrls.RentProfile
  if (isTeam(s)) return `${RedirectUrls.Team}/${s.selectedTeamId}`
  if (s.selectedTeamId === 'none' || s.selectedHostId) {
    return RedirectUrls.HostProfile
  }
  return RedirectUrls.Team
}

const byRole =
  (host: string, rent: string) =>
  (role: DashboardRole): string =>
    role === 'host' ? host : rent

const same = (icon: string) => () => icon

export const NAV_ITEMS: Record<NavKey, NavItemDef> = {
  hoy: {
    key: 'hoy',
    labelKey: 'SHELL.HOY',
    icon: same('si-dashboard'),
    href: (s) =>
      s.role === 'host' ? RedirectUrls.Dashboard : RedirectUrls.DashboardRent,
    badge: () => 0
  },
  rentas: {
    key: 'rentas',
    labelKey: 'SHELL.RENTAS',
    icon: byRole('si-reservations', 'si-my-rentals'),
    href: (s) =>
      s.role === 'host' ? RedirectUrls.Rents : RedirectUrls.Reservations,
    badge: (c, role) =>
      role === 'host'
        ? c.rents + c.missingFacturas + c.missingRefundDocs
        : c.reservations
  },
  interesados: {
    key: 'interesados',
    labelKey: 'SHELL.INTERESADOS',
    icon: same('si-user-single'),
    href: () => RedirectUrls.HostInteresados,
    badge: () => 0
  },
  espacios: {
    key: 'espacios',
    labelKey: 'SHELL.ESPACIOS',
    icon: same('si-warehouse'),
    href: () => RedirectUrls.Listings,
    badge: (c) => c.locations
  },
  tratos: {
    key: 'tratos',
    labelKey: 'SHELL.TRATOS',
    icon: same('si-bill'),
    href: () => RedirectUrls.HostClosedDeals,
    badge: (c) => c.pendingClosedDeals
  },
  mensajes: {
    key: 'mensajes',
    labelKey: 'SHELL.MENSAJES',
    icon: same('si-chat'),
    href: (s) =>
      s.role === 'host' ? RedirectUrls.HostMessages : RedirectUrls.RentMessages,
    badge: (c) => c.messages
  },
  inventario: {
    key: 'inventario',
    labelKey: 'SHELL.INVENTARIO',
    icon: same('si-circle-check'),
    href: () => RedirectUrls.HostInventario,
    badge: (c) => c.inventoryReviewPending
  },
  pagos: {
    key: 'pagos',
    labelKey: 'SHELL.PAGOS',
    icon: same('si-payment'),
    href: () => RedirectUrls.HostPayments,
    // Factura gaps already badge Rentas — counting them twice would read as
    // two different chores.
    badge: () => 0
  },
  cuenta: {
    key: 'cuenta',
    labelKey: 'SHELL.CUENTA',
    icon: same('si-user'),
    href: cuentaHref,
    // Fiscal is a host duty (retention); the renter nav badged profile only.
    badge: (c, role) => (role === 'host' ? c.profile + c.fiscal : c.profile)
  },
  /**
   * Platform admins on "Todas" only (navRules swaps it in for `cuenta`): staff
   * run every client's account, and the teams list shows them all.
   */
  cuentas: {
    key: 'cuentas',
    labelKey: 'SHELL.CUENTAS',
    icon: same('si-commercial'),
    href: () => RedirectUrls.Team,
    badge: () => 0
  },
  admin: {
    key: 'admin',
    labelKey: 'SHELL.ADMIN',
    icon: same('si-sliders'),
    href: () => RedirectUrls.Admin,
    badge: (c) => c.admin
  }
}

/** Which RedirectUrls (from PATHNAME_TO_REDIRECT_URL) light up each item. */
export const DASHBOARD_ROUTE_KEYS: Record<NavKey, RedirectUrls[]> = {
  hoy: [RedirectUrls.Dashboard, RedirectUrls.DashboardRent],
  rentas: [
    RedirectUrls.Rents,
    RedirectUrls.Reservations,
    RedirectUrls.CheckoutDetails
  ],
  interesados: [RedirectUrls.HostInteresados],
  espacios: [RedirectUrls.Listings],
  tratos: [RedirectUrls.HostClosedDeals],
  mensajes: [RedirectUrls.HostMessages, RedirectUrls.RentMessages],
  inventario: [RedirectUrls.HostInventario],
  pagos: [RedirectUrls.HostPayments],
  cuenta: [
    RedirectUrls.HostProfile,
    RedirectUrls.RentProfile,
    RedirectUrls.Team
  ],
  cuentas: [RedirectUrls.Team],
  admin: [RedirectUrls.Admin]
}
